/* eslint-disable max-len */
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { NgSelectModule } from '@ng-select/ng-select';
import { ErrorTailorModule } from '@ngneat/error-tailor';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { SwiperModule } from 'swiper/angular';

import { DirectivesModule } from '../core/directives/directives.module';
import { LocalizedDatePipe } from '../core/pipes/localizedDatePipe.pipe';
import { AlternativeOfferDialogComponent } from './components/alternative-offer-dialog/alternative-offer-dialog.component';
import { BannerComponent } from './components/banner/banner.component';
import { CarservicesBeforebuyDialogComponent } from './components/carservices-beforebuy-dialog/carservices-beforebuy-dialog-component';
import { CarservicesDiscountDialogComponent } from './components/carservices-discount-dialog/carservices-discount-dialog.component';
import { ChallengeDialogComponent } from './components/challenge-dialog/challenge-dialog.component';
import { ChangeUserModeDialogComponent } from './components/change-user-mode-dialog/change-user-mode-dialog.component';
import { CookiesAdviceComponent } from './components/cookies-advice/cookies-advice.component';
import { DateDialogComponent } from './components/date-dialog/date-dialog.component';
import { DialogBannerComponent } from './components/dialog-banner/dialog-banner.component';
import { FeedbackDialogComponent } from './components/feedback-dialog/feedback-dialog.component';
import { FooterComponent } from './components/footer/footer.component';
import { GmapsDialogComponent } from './components/gmaps-dialog/gmaps-dialog.component';
import { GmapsComponent } from './components/gmaps/gmaps.component';
import { HeaderComponent } from './components/header/header.component';
import { HourPickerComponent } from './components/hour-picker/hour-picker.component';
import { InfoSectionDialogComponent } from './components/info-section-dialog/info-section-dialog.component';
import { InitialBookingDataDialogComponent } from './components/initial-booking-data-dialog/initial-booking-data-dialog.component';
import { LanguageSelectorComponent } from './components/language-selector/language-selector.component';
import { LinkReservationDialogComponent } from './components/link-reservation-dialog/link-reservation-dialog.component';
import { LoadingComponent } from './components/loading/loading.component';
import { LoginDialogComponent } from './components/login-dialog/login-dialog.component';
import { NavTabsComponent } from './components/nav-tabs/nav-tabs.component';
import { RecoveryPassDialogComponent } from './components/recovery-pass-dialog/recovery-pass-dialog.component';
import { SeparatorComponent } from './components/separator/separator.component';
import { SignUpDialogComponent } from './components/sign-up-dialog/sign-up-dialog.component';
import { SummaryBookingDataComponent } from './components/summary-booking-data/summary-booking-data.component';
import { SummaryDiscountsComponent } from './components/summary-discounts/summary-discounts.component';
import { SummaryLocationComponent } from './components/summary-location/summary-location.component';
import { SummaryPriceComponent } from './components/summary-price/summary-price.component';
import { SummaryReservationComponent } from './components/summary-reservation/summary-reservation.component';
import { SummaryVehicleComponent } from './components/summary-vehicle/summary-vehicle.component';
import { SummaryComponent } from './components/summary/summary.component';
import { TripInfoPassengersComponent } from './components/trip-info-passengers/trip-info-passengers.component';
import { TripInfoReturnFlightComponent } from './components/trip-info-return-flight/trip-info-return-flight.component';
import { TripInfoTerminalComponent } from './components/trip-info-terminal/trip-info-terminal.component';
import { VehicleDialogLicensePlateComponent } from './components/vehicle-dialog-license-plate/vehicle-dialog-license-plate.component';
import { VehicleDialogModifyDataComponent } from './components/vehicle-dialog-modify-data/vehicle-dialog-modify-data.component';
import { VehicleDialogModifyKmComponent } from './components/vehicle-dialog-modify-km/vehicle-dialog-modify-km.component';
import { VehicleDialogNotFoundFormComponent } from './components/vehicle-dialog-not-found-form/vehicle-dialog-not-found-form.component';
import { VehicleDialogRequestedDataComponent } from './components/vehicle-dialog-requested-data/vehicle-dialog-requested-data.component';
import { VehicleDialogSelectClientCarComponent } from './components/vehicle-dialog-select-client-car/vehicle-dialog-select-client-car.component';
import { VehicleDialogUmcDataSelectorsComponent } from './components/vehicle-dialog-umc-data-selectors/vehicle-dialog-umc-data-selectors.component';
import { VehicleDialogUmcSelectorsComponent } from './components/vehicle-dialog-umc-selectors/vehicle-dialog-umc-selectors.component';
import { VehicleDialogComponent } from './components/vehicle-dialog/vehicle-dialog.component';
import { VehicleWarningDialogComponent } from './components/vehicle-warning-dialog/vehicle-warning-dialog-component';
import { TranslatedErrors } from './utils/errors-translations';
import { SummaryVouchersComponent } from './components/summary-vouchers/summary-vouchers.component';
import { CouponComponent } from './components/coupon/coupon.component';
import { VoucherBannerComponent } from './components/voucher-banner/voucher-banner.component';
import { VoucherListComponent } from './components/voucher-list/voucher-list.component';

const ANGULAR_MODULES = [
  CommonModule,
  ReactiveFormsModule,
  FormsModule,
  RouterModule,
];

const THIRD_PARTY_LIBRARY = [TranslateModule, ErrorTailorModule, SwiperModule];

const PUBLIC_COMPONENTS = [
  BannerComponent,
  DialogBannerComponent,
  FooterComponent,
  HeaderComponent,
  InfoSectionDialogComponent,
  InitialBookingDataDialogComponent,
  LanguageSelectorComponent,
  LoginDialogComponent,
  NavTabsComponent,
  SeparatorComponent,
  SignUpDialogComponent,
  SummaryBookingDataComponent,
  SummaryComponent,
  SummaryDiscountsComponent,
  SummaryLocationComponent,
  SummaryPriceComponent,
  SummaryVehicleComponent,
  LoadingComponent,
  SummaryReservationComponent,
  DialogBannerComponent,
  VehicleDialogComponent,
  VehicleDialogUmcSelectorsComponent,
  VehicleDialogNotFoundFormComponent,
  VehicleDialogLicensePlateComponent,
  VehicleDialogSelectClientCarComponent,
  VehicleDialogRequestedDataComponent,
  VehicleDialogUmcDataSelectorsComponent,
  VehicleWarningDialogComponent,
  VehicleDialogModifyKmComponent,
  VehicleDialogModifyDataComponent,
  HourPickerComponent,
  CookiesAdviceComponent,
  DateDialogComponent,
  RecoveryPassDialogComponent,
  FeedbackDialogComponent,
  GmapsComponent,
  GmapsDialogComponent,
  LinkReservationDialogComponent,
  TripInfoPassengersComponent,
  TripInfoReturnFlightComponent,
  CarservicesDiscountDialogComponent,
  AlternativeOfferDialogComponent,
  LocalizedDatePipe,
  ChangeUserModeDialogComponent,
  ChallengeDialogComponent,
  TripInfoTerminalComponent,
  CarservicesBeforebuyDialogComponent,
  CouponComponent,
  SummaryVouchersComponent,
  VoucherBannerComponent,
  VoucherListComponent
];

@NgModule({
  declarations: [...PUBLIC_COMPONENTS,],
  imports: [
    ...ANGULAR_MODULES,
    ...THIRD_PARTY_LIBRARY,
    DirectivesModule,
    NgSelectModule,
    ErrorTailorModule.forRoot({
      errors: {
        useFactory: TranslatedErrors,
        deps: [TranslateService],
      },
      blurPredicate: (element: Element) => {
        return ['INPUT', 'NG-SELECT'].some(
          (selector: string) => element.tagName === selector
        );
      },
    }),
  ],
  exports: [
    ...ANGULAR_MODULES,
    ...THIRD_PARTY_LIBRARY,
    ...PUBLIC_COMPONENTS,
    DirectivesModule,
  ],
})
export class SharedModule { }
